import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, inject } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { DateUtility } from '../../date.utility';
import { DateFormatType } from '../../enums/date-format-type.enum';
import { ItemOutCP } from '../../models/item-out-model';
import { RowLabelValue } from '../../models/row-label-value-model';
import { LineItemStatus } from '../../enums/line-item-status.enum';
import { AppFacadeService } from '../../services/app-facade.service';
import { isEmpty } from 'lodash';
import { FeatureToggleService } from '../../services/feature-toggle.service';
import { DepotTimezoneFormatterService } from '../../services/depot-timezone-formatter.service';
import { OrganizationType } from '../../models/organization-type';
import { OrganizationDepot } from '../../models/organization-depot.model';
import { DepotTimezoneDateFormat } from '../../enums/depot-timezone-date-format.enum';
import { Subscription } from 'rxjs';
@Component({
    standalone: false,
    selector: 'por-items-out-full-width-row',
    templateUrl: './items-out-full-width-row.component.html',
    styleUrls: ['./items-out-full-width-row.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ItemsOutFullWidthRowComponent implements ICellRendererAngularComp, OnDestroy {
    private readonly date = inject(DatePipe);
    readonly appFacadeService = inject(AppFacadeService);
    private readonly featureToggleService = inject(FeatureToggleService);
    private readonly depotTimezoneFormatter = inject(DepotTimezoneFormatterService);

    public params!: ICellRendererParams;
    checkboxSelected = false;
    isDisabled = false;

    itemOut!: ItemOutCP;
    itemOutKV: RowLabelValue[] = [];
    dateFormat = '';
    displayLocationColumn = false;

    // Store config for timezone formatting
    private organizationType: OrganizationType | undefined;
    private depots: OrganizationDepot[] | undefined;
    private locale = 'en-US'; // Default to US locale

    // Subscription management
    private readonly subscription: Subscription = new Subscription();

    constructor() {
        this.dateFormat = DateUtility.getDateDisplayFormat(DateFormatType.StandardDate, 'en-US');

        // Subscribe to config to get organizationType, depots, and locale
        this.subscription.add(
            this.appFacadeService.getConfig().subscribe(config => {
                this.organizationType = config?.organizationType as OrganizationType | undefined;
                this.depots = config?.depots;
                // Get locale from organization config (e.g., 'en-GB' for Syrinx, 'en-US' for Elite)
                this.locale = config?.Locale || 'en-US';
            })
        );
    }
    agInit(params: ICellRendererParams): void {
        this.itemOut = { ...params.data };
        this.params = params;
        this.isDisabled = params.data && params.data.lineItemStatus === LineItemStatus.Out ? false : true;
        this.itemOutKV = [
            {
                label: 'itemName',
                value: params.data?.itemName
            },
            {
                label: 'contract',
                value: this.featureToggleService.isSyrinx() ? params.data?.contractName : params.data?.contractId // 84603 My hires contract name
            },
            {
                label: 'Quantity',
                value: params.data?.quantity
            },
            {
                label: 'startDate',
                value: this.formatDateWithTimezone(params.data?.startDate, params.data?.depotId)
            },
            {
                label: 'dueDate',
                value: this.formatDateWithTimezone(params.data?.dueDate, params.data?.depotId)
            },
            ...(!this.featureToggleService.isSyrinx()
                ? [
                      {
                          label: 'poNumber',
                          value: params.data?.customerPurchaseOrder
                      }
                  ]
                : []),
            ...(!this.featureToggleService.isSyrinx()
                ? [
                      {
                          label: 'jobNr',
                          value: params.data?.customerJobNumber
                      }
                  ]
                : [])
        ];

        if (!isEmpty(params.data?.info)) {
            this.itemOutKV.push({
                label: 'Depot',
                value: params.data?.info
            });
        }
    }
    private formatDateWithTimezone(date: string | number | Date | null | undefined, depotId: string | undefined): string | null {
        if (!date) {
            return null;
        }

        // Get date only (MM/DD/YYYY for US, DD/MM/YYYY for UK)
        const dateOnlyStr = this.depotTimezoneFormatter.format(date, DepotTimezoneDateFormat.DateOnly, this.organizationType, this.depots, depotId, this.locale);

        // Syrinx systems should not show timezone
        if (this.featureToggleService.isSyrinx()) {
            return dateOnlyStr;
        }

        // Get timezone abbreviation from full format
        const fullStr = this.depotTimezoneFormatter.format(date, DepotTimezoneDateFormat.Full, this.organizationType, this.depots, depotId, this.locale);
        const timezoneMatch = fullStr.match(/\s([A-Z]{3,4})$/);
        const timezone = timezoneMatch ? timezoneMatch[1] : '';

        // Combine: "12/2/2025 CST" (US) or "2/12/2025 GMT" (UK)
        return timezone ? `${dateOnlyStr} ${timezone}` : dateOnlyStr;
    }

    refresh() {
        return true;
    }

    loadRequestmodal() {
        this.params.context?.componentParent.loadRequestmodal(this.itemOut);
    }

    checkedHandler(check: boolean) {
        const accepted = this.params.context.componentParent.setMobileCheckboxandItems(check, this.itemOut);
        if (accepted !== check) {
            // The parent rejected the tap (off-rent selection cap). mat-checkbox owns its own checked
            // state here — re-writing the unchanged [isChecked] binding can't untick it — so redraw
            // just this row to bring the tick back in line with the actual selection.
            this.params.api.redrawRows({ rowNodes: [this.params.node] });
        }
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }
}
